import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { formatCurrency } from '../../lib/formatters';
import { toast } from 'sonner';
import { Scale, Save } from 'lucide-react';
import ParticipationCard from './ParticipationCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getStatusColor = (status) => {
  const colors = {
    'Funded': 'bg-green-100 text-green-800',
    'Payment Issues': 'bg-yellow-100 text-yellow-800',
    'In-House Collection': 'bg-orange-100 text-orange-800',
    'Default': 'bg-red-100 text-red-800',
    'Collected': 'bg-blue-100 text-blue-800',
    'Paid in Full': 'bg-purple-100 text-purple-800',
    'Paid in Full (Refinanced)': 'bg-blue-100 text-blue-800',
    'Paid in Full / Settled': 'bg-teal-100 text-teal-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const formatDate = (d) => {
  if (!d) return 'N/A';
  const dt = new Date(d);
  return `${String(dt.getUTCMonth()+1).padStart(2,'0')}/${String(dt.getUTCDate()).padStart(2,'0')}/${dt.getUTCFullYear()}`;
};

const LEGAL_STATUSES = [
  'No Case',
  'Pre-Litigation',
  'Litigation Filed',
  'Judgment Obtained',
  'In Collections',
  'Settled',
  'Closed',
];

const LegalMetadataSection = ({ deal, getAuthHeaders, onRefresh }) => {
  const [form, setForm] = useState({
    legal_status: deal?.legal_status || '',
    legal_lawyer_name: deal?.legal_lawyer_name || '',
    legal_assigned_at: deal?.legal_assigned_at || '',
    legal_notes: deal?.legal_notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/deals/${deal.id}`, form, { headers: getAuthHeaders() });
      toast.success('Legal metadata saved');
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save legal metadata');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="legal-metadata-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Scale className="h-4 w-4" /> Legal & Default Metadata
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Legal Status</Label>
            <Select value={form.legal_status || 'none'} onValueChange={v => setForm(p => ({...p, legal_status: v === 'none' ? '' : v}))}>
              <SelectTrigger data-testid="legal-status-select"><SelectValue placeholder="Select status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">-- None --</SelectItem>
                {LEGAL_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Lawyer / Law Firm</Label>
            <Input
              data-testid="legal-lawyer-input"
              value={form.legal_lawyer_name}
              onChange={e => setForm(p => ({...p, legal_lawyer_name: e.target.value}))}
              placeholder="Lawyer or firm name"
            />
          </div>
          <div>
            <Label className="text-xs">Assigned Date</Label>
            <Input
              data-testid="legal-assigned-date-input"
              type="date"
              value={form.legal_assigned_at}
              onChange={e => setForm(p => ({...p, legal_assigned_at: e.target.value}))}
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">Legal Notes</Label>
          <Textarea
            data-testid="legal-notes-input"
            value={form.legal_notes}
            onChange={e => setForm(p => ({...p, legal_notes: e.target.value}))}
            placeholder="Notes about legal proceedings..."
            rows={2}
          />
        </div>
        <Button data-testid="save-legal-metadata-btn" onClick={handleSave} disabled={saving} size="sm">
          <Save className="h-3 w-3 mr-1" /> {saving ? 'Saving...' : 'Save Legal Metadata'}
        </Button>
      </CardContent>
    </Card>
  );
};

const DetailsTab = ({ deal, getAuthHeaders, onRefresh }) => {
  const isDefaulted = deal?.status === 'Default';

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Deal Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-semibold mb-3">Basic Information</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Deal ID:</span>
                  <span>{deal.deal_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Shortcode:</span>
                  <span>{deal.deal_shortcode}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <Badge className={getStatusColor(deal.status)}>{deal.status}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Position:</span>
                  <span>{deal.position || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Funding Date:</span>
                  <span>{formatDate(deal.funding_date)}</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-3">Financial Terms</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Funding Amount:</span>
                  <span>{formatCurrency(deal.funding_amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Factor Rate:</span>
                  <span>{deal.rate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payback:</span>
                  <span>{formatCurrency(deal.payback)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Frequency:</span>
                  <span>{deal.payment_frequency || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Amount:</span>
                  <span>{formatCurrency(deal.payment_amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Term Days:</span>
                  <span>{deal.term_days || 'N/A'}</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-3">Commission</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Commission Type:</span>
                  <span>{deal.commission_type || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Commission Rate:</span>
                  <span>{deal.commission_rate || 0}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Broker Commission:</span>
                  <span>{formatCurrency(deal.broker_commission)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Origination Fee:</span>
                  <span>{formatCurrency(deal.fees)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Owner & Business info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-4 border-t">
            <div>
              <h3 className="font-semibold mb-3">Owner Information</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Owner Name:</span>
                  <span>{deal.owner_name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Owner Phone:</span>
                  <span>{deal.owner_phone || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Owner Email:</span>
                  <span>{deal.owner_email || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Owner Address:</span>
                  <span className="text-right max-w-[200px]">{deal.owner_address || 'N/A'}</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-3">Business Information</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Legal Business Name:</span>
                  <span>{deal.legal_business_name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">EIN / Tax ID:</span>
                  <span>{deal.ein_tax_id || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Business Type:</span>
                  <span>{deal.business_type || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Monthly Revenue:</span>
                  <span>{deal.monthly_revenue ? formatCurrency(deal.monthly_revenue) : 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legal Metadata — shown for defaulted deals */}
      {isDefaulted && getAuthHeaders && (
        <LegalMetadataSection deal={deal} getAuthHeaders={getAuthHeaders} onRefresh={onRefresh} />
      )}

      {/* 09/29 (#27 E): outbound participation sheet — lettered deals (PFS into another funder's deal) */}
      {getAuthHeaders && <ParticipationCard deal={deal} getAuthHeaders={getAuthHeaders} onRefresh={onRefresh} />}
    </div>
  );
};

export default DetailsTab;
